import { useCallback, useRef, useState } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { entityTypeLabelKey } from '../../../generic/qr/qrEntityTypes'
import { QR_ACTIONS, QR_OUTCOMES, failureOutcome, scanQrCode } from './qrLookupApi'

/**
 * Turns a read label into either a result to render or a message for the operator.
 *
 * The message matters as much as the result: the reading dialog keeps itself open when `onScan`
 * resolves to a non-empty string, so returning the right text here is what lets an operator scan
 * again without re-opening anything. Every message is a translation key of our own — the backend's
 * `reason` is for logs, never for the shop floor.
 */
const useQrLookup = ({ deviceId = 0, workerId = 0, expectedEntityType = 'Any', action = QR_ACTIONS.SHEET, jobPolicies = {} } = {}) => {
  const { t_ } = useSmartTranslation()
  const [lastResult, setLastResult] = useState({})
  // Only the answer of the latest read counts: Sun may reply out of order, and a read the operator
  // walked away from must not overwrite or open anything.
  const latestRead = useRef(0)
  const [pending, setPending] = useState(false)

  // Forgets the read in flight, if any: its answer will come back as stale.
  const cancelPending = useCallback(() => {
    latestRead.current += 1
    setPending(false)
  }, [])

  const messageFor = useCallback((error) => {
    switch (failureOutcome(error)) {
      case QR_OUTCOMES.NOT_A_MIC_QR:
        return t_('Not a MIC QR code')
      case QR_OUTCOMES.UNEXPECTED_TYPE:
        return t_('The scanned code does not match the expected type') + ': ' +
          t_(entityTypeLabelKey(expectedEntityType))
      case QR_OUTCOMES.NOT_FOUND:
        // Also how a label of an element that has been deactivated comes back: the id no longer
        // resolves to anything usable, and for the operator that is the same event.
        return t_('The scanned element was not found')
      case QR_OUTCOMES.UNSUPPORTED_TYPE:
        return t_('Unsupported entity type')
      case QR_OUTCOMES.INVALID_REQUEST:
        // A MIC label whose head carries no instance id: readable, but it points at nothing.
        return t_('The scanned code is incomplete')
      default:
        return t_('The scanned code could not be processed')
    }
  }, [t_, expectedEntityType])

  /**
   * @returns {Promise<{result?: object, message?: string, stale?: boolean}>} `message` set means the label
   *   could not be used and the reading dialog must stay open. `stale` means a later read (or a cancel)
   *   superseded this one: the caller must do nothing with it.
   */
  const scan = useCallback(async ({ parsed }) => {
    latestRead.current += 1
    const read = latestRead.current
    setPending(true)
    try {
      const result = await scanQrCode({ parsed, expectedEntityType, deviceId, workerId, action, jobPolicies })
      if (read !== latestRead.current) return { stale: true }
      setLastResult({ ...result, at: new Date().toLocaleTimeString() })
      return { result }
    } catch (error) {
      if (read !== latestRead.current) return { stale: true }
      return { message: messageFor(error) }
    } finally {
      if (read === latestRead.current) setPending(false)
    }
  }, [expectedEntityType, deviceId, workerId, action, jobPolicies, messageFor])

  return { scan, lastResult, pending, cancelPending }
}

export default useQrLookup
